import type React from 'react';

import { CssBaseline } from '@mui/material';
import { ThemeProvider } from '@mui/material/styles';
import * as Sentry from '@sentry/react';
import { useQueryClient } from '@tanstack/react-query';
import {
  createRouteMask,
  createRouter,
  RouterProvider,
} from '@tanstack/react-router';
import { AuthProvider } from 'oidc-react';
import { useEffect, useState } from 'react';
import { Provider as ReduxProvider } from 'react-redux';

import store from '~/redux/store';

import ThirdPartyService from '~/services/thirdParty.service';

import { LoadingPage, RootErrorBoundary } from '~/modules/router/components';

import { HotkeysProvider } from '@vestigas/utils';
import '~/utils/i18n';
import { isPublicRoute } from '~/utils/route';

import '@vestigas/ui/index.css';
import { theme } from '~/ui/styles';
import '~/ui/styles/index.css';
import '~/ui/styles/legacy.css';

import { BugReportWidget } from './modules/bugReport';
import { routeTree } from './routeTree.gen';
import AuthService from './services/auth.service';

const externalSupplierMask = createRouteMask({
  routeTree,
  from: '/external-supplier',
  to: '/',
});

const pickupSupplierMask = createRouteMask({
  routeTree,
  from: '/pickup',
  to: '/',
});

const routeMasks = [externalSupplierMask, pickupSupplierMask];

// Create a new router instance
const router = createRouter({
  context: {
    auth: undefined!,
    queryClient: undefined!,
  },
  defaultErrorComponent: () => <RootErrorBoundary type="generic" />,
  defaultNotFoundComponent: () => <RootErrorBoundary type="404" />,
  defaultOnCatch(error, errorInfo) {
    // * We have a fingerprinting rule set up in Sentry UI, to rename all boundary issues to "React Boundary Error"
    Sentry.captureReactException(error, errorInfo, {
      level: 'fatal',
    });
  },
  defaultPendingComponent: () => <LoadingPage />,
  routeMasks,
  routeTree,
});

// Register the router instance for type safety.
declare module '@tanstack/react-router' {
  interface Register {
    router: typeof router;
  }
  interface StaticDataRouteOption {
    AppBar?: {
      title?: string;
      CustomComponent?: React.ComponentType;
    };
  }
}

ThirdPartyService.initMuiPro();
ThirdPartyService.initSentry(router);

function RouterProviderWithContext() {
  const auth = AuthService;
  const queryClient = useQueryClient();

  return (
    <RouterProvider
      router={router}
      context={{
        auth,
        queryClient,
      }}
    />
  );
}

// Check for public route before AuthProvider mounts to prevent auto-redirect to Keycloak
// Public routes (see PUBLIC_ROUTE_PREFIXES, e.g. /calloff/respond/) should not trigger auto-signin
// OIDC callback handling is done internally by AuthService.getOidcConfig()
const shouldAutoSignIn = !isPublicRoute(globalThis.location.pathname);

function App() {
  // Before the AuthProvider can auto-redirect to Keycloak, try to silently
  // restore the session from the stored refresh token. Without this, every
  // browser restart (sessionStorage wiped) forces a visible re-login even
  // though the refresh token is still valid.
  const [isRestoringSession, setIsRestoringSession] =
    useState(shouldAutoSignIn);

  useEffect(() => {
    if (!shouldAutoSignIn) {
      return;
    }

    void AuthService.restoreSession().finally(() => {
      setIsRestoringSession(false);
    });
  }, []);

  if (isRestoringSession) {
    return <LoadingPage />;
  }

  return (
    <ReduxProvider store={store}>
      <HotkeysProvider>
        <ThemeProvider theme={theme}>
          <CssBaseline />
          <AuthProvider {...AuthService.getOidcConfig(shouldAutoSignIn)}>
            <RouterProviderWithContext />
            <BugReportWidget />
          </AuthProvider>
        </ThemeProvider>
      </HotkeysProvider>
    </ReduxProvider>
  );
}

export default Sentry.withProfiler(App);
